import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Modal from "../../../components/base/Modal";
import { Badge, DIALOG_COLORS, cn } from "../../../components/ui";
import { buildThemeClasses, APPT_STATUS_TONES } from "../theme";
import { useVehicles } from "../hooks/useVehicles";
import { useMxLabels } from "../hooks/useMxLabels";
import {
  createAppointment,
  deleteAppointment,
  fetchAppointments,
  fetchScheduleConfig,
  fetchServiceCatalog,
  fetchServiceRates,
  updateAppointment,
  updateScheduleConfig,
} from "../services/maintenanceService";
import type { AppointmentStatus, MaintenanceAppointment, ServiceCatalogItem, WorkshopScheduleConfig } from "../types";
import AppointmentModal from "../components/AppointmentModal";
import ScheduleConfigModal from "../components/ScheduleConfigModal";
import { fmtTime } from "../lib";
import { ErrorBlock, KpiCard, LoadingBlock, PageShell, ToolbarButton, ViewSwitch } from "../components/kit/Primitives";
import { Photo, VehicleThumb } from "../components/kit/Media";

const pad = (n: number) => String(n).padStart(2, "0");
const keyOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = () => keyOf(new Date());

function shiftDay(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return keyOf(new Date(y, m - 1, d + days));
}

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const HOUR_PX = 64;

const BLOCK_TONES: Record<string, string> = {
  reservado: "border-info/60 bg-info/10",
  confirmado: "border-success/60 bg-success/10",
  en_proceso: "border-warning/70 bg-warning/15",
  completado: "border-line bg-subtle/70 opacity-80",
  cancelado: "border-error/40 bg-error/5 opacity-60 line-through",
};

type View = "bays" | "list";

// Agenda del taller por bahía: una columna por bahía y una fila por hora del
// horario configurado; cada cita es un bloque con la foto de la unidad. Se
// reserva, confirma, reprograma y cancela; la disponibilidad y el candado
// contra sobrecupo siguen en el backend y en el modal de reserva.
export default function AgendaSection() {
  const L = useMxLabels();
  const { t } = L;
  const tc = buildThemeClasses();
  const { vehicles, vehicleOf } = useVehicles();

  const [date, setDate] = useState(todayKey());
  const [view, setView] = useState<View>("bays");
  const [config, setConfig] = useState<WorkshopScheduleConfig | null>(null);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [rows, setRows] = useState<MaintenanceAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const [modal, setModal] = useState<null | { kind: "reserve" } | { kind: "reschedule"; appt: MaintenanceAppointment } | { kind: "config" }>(null);

  const loadDay = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchAppointments({ date }));
    } catch {
      setError(t("mantenimiento.loadError"));
    } finally {
      setLoading(false);
    }
  }, [date, t]);

  // Config + catálogo (servicios del POS y tabulador del taller) una vez.
  useEffect(() => {
    let alive = true;
    Promise.all([fetchScheduleConfig(), fetchServiceCatalog().catch(() => []), fetchServiceRates().catch(() => [])])
      .then(([cfg, svc, rates]) => {
        if (!alive) return;
        setConfig(cfg);
        const fromRates: ServiceCatalogItem[] = rates.filter((r) => r.active !== false).map((r) => ({ id: r.id, name: r.name, description: r.description, price: r.price, currency: r.currency, durationMinutes: r.durationMinutes, sku: r.sku, type: "service", active: true }));
        setServices([...fromRates, ...svc.filter((s) => s.active !== false)]);
      })
      .catch(() => alive && setConfig(null));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(id);
  }, []);

  const active = useMemo(() => rows.filter((r) => r.status !== "cancelado"), [rows]);
  const sorted = useMemo(() => [...rows].sort((a, b) => a.start.localeCompare(b.start)), [rows]);
  const bays = Math.max(config?.bays || 1, ...rows.map((r) => Number(r.bay) || 0));
  const open = toMinutes(config?.openTime || "08:00");
  const close = toMinutes(config?.closeTime || "18:00");
  const hours = Array.from({ length: Math.ceil((close - open) / 60) }, (_, i) => open + i * 60);
  const minutesOf = (iso: string) => {
    const d = new Date(iso);
    return d.getHours() * 60 + d.getMinutes();
  };
  const busyMinutes = active.reduce((s, r) => s + (Number(r.durationMinutes) || 0), 0);
  const occupancy = config ? Math.min(100, Math.round((busyMinutes / (Math.max(1, close - open) * bays)) * 100)) : 0;
  const isToday = date === todayKey();
  const nowLine = isToday ? (new Date(now).getHours() * 60 + new Date(now).getMinutes() - open) / 60 * HOUR_PX : null;
  const selected = rows.find((r) => r.id === selectedId) || null;

  const handleCreate = async (values: Partial<MaintenanceAppointment>) => {
    await createAppointment(values);
    setModal(null);
    await loadDay();
    Swal.fire({ icon: "success", title: t("mantenimiento.agenda.booked"), timer: 1600, showConfirmButton: false });
  };

  const handleReschedule = async (id: string, values: Partial<MaintenanceAppointment>) => {
    await updateAppointment(id, values);
    setModal(null);
    await loadDay();
  };

  const setStatus = async (appt: MaintenanceAppointment, status: AppointmentStatus) => {
    try {
      await updateAppointment(appt.id, { status });
      await loadDay();
    } catch {
      Swal.fire({ icon: "error", title: t("mantenimiento.agenda.bookingError") });
    }
  };

  const handleCancel = async (appt: MaintenanceAppointment) => {
    const res = await Swal.fire({
      title: t("mantenimiento.agenda.cancelTitle"),
      text: t("mantenimiento.agenda.cancelText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("mantenimiento.agenda.cancelConfirm"),
      cancelButtonText: t("mantenimiento.cancel"),
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    await setStatus(appt, "cancelado");
  };

  const handleDelete = async (appt: MaintenanceAppointment) => {
    const res = await Swal.fire({
      title: t("mantenimiento.confirmDeleteTitle"),
      text: t("mantenimiento.confirmDeleteText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("mantenimiento.delete"),
      cancelButtonText: t("mantenimiento.cancel"),
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    await deleteAppointment(appt.id);
    setSelectedId(null);
    await loadDay();
  };

  const handleSaveConfig = async (patch: Partial<WorkshopScheduleConfig>) => {
    const saved = await updateScheduleConfig(patch);
    setConfig(saved);
    setModal(null);
    await loadDay();
  };

  const dayLabel = useMemo(() => {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(L.locale, { weekday: "long", day: "numeric", month: "long" });
  }, [date, L.locale]);

  // Tira de la semana (lunes a domingo) del día elegido.
  const week = useMemo(() => {
    const [y, m, d] = date.split("-").map(Number);
    const base = new Date(y, m - 1, d);
    const monday = new Date(base);
    monday.setDate(base.getDate() - ((base.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(monday);
      x.setDate(monday.getDate() + i);
      return { key: keyOf(x), dow: x.toLocaleDateString(L.locale, { weekday: "short" }), day: x.getDate(), work: config ? config.workDays.includes(x.getDay()) : true };
    });
  }, [date, L.locale, config]);

  const ApptActions = ({ appt }: { appt: MaintenanceAppointment }) => {
    const closed = appt.status === "completado" || appt.status === "cancelado";
    return (
      <div className="flex flex-wrap items-center gap-1">
        {!closed && appt.status !== "confirmado" ? (
          <button type="button" onClick={() => setStatus(appt, "confirmado")} title={t("mantenimiento.agenda.confirm")} className="rounded-md p-1.5 text-success hover:bg-success/10"><i className="ri-check-line" /></button>
        ) : null}
        {!closed ? (
          <button type="button" onClick={() => setModal({ kind: "reschedule", appt })} title={t("mantenimiento.agenda.reschedule")} className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"><i className="ri-calendar-event-line" /></button>
        ) : null}
        {!closed ? (
          <button type="button" onClick={() => handleCancel(appt)} title={t("mantenimiento.agenda.cancelAppointment")} className="rounded-md p-1.5 text-warning hover:bg-warning/10"><i className="ri-close-circle-line" /></button>
        ) : null}
        <button type="button" onClick={() => handleDelete(appt)} title={t("mantenimiento.delete")} className="rounded-md p-1.5 text-error hover:bg-error/10"><i className="ri-delete-bin-line" /></button>
      </div>
    );
  };

  return (
    <PageShell
      icon="ri-calendar-check-line"
      title={t("mantenimiento.menu.agenda")}
      subtitle={t("mantenimiento.agendaSubtitle")}
      actions={
        <>
          <ToolbarButton icon="ri-settings-3-line" label={t("mantenimiento.agenda.settings")} onClick={() => setModal({ kind: "config" })} disabled={!config} />
          <ToolbarButton primary icon="ri-add-line" label={t("mantenimiento.agenda.reserve")} onClick={() => setModal({ kind: "reserve" })} disabled={!config} />
        </>
      }
      toolbar={
        <>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setDate((d) => shiftDay(d, -7))} className="rounded-lg p-2 text-content-secondary hover:bg-subtle" title="−7"><i className="ri-arrow-left-double-line" /></button>
            <button type="button" onClick={() => setDate((d) => shiftDay(d, -1))} className="rounded-lg p-2 text-content-secondary hover:bg-subtle"><i className="ri-arrow-left-s-line" /></button>
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {week.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setDate(d.key)}
                className={cn(
                  "flex w-12 flex-col items-center rounded-lg border py-1 text-xs transition-colors",
                  d.key === date ? "border-primary-500 bg-primary-500 text-on-primary" : d.key === todayKey() ? "border-primary-500/50 text-primary-500" : "border-line bg-card text-content-secondary hover:bg-subtle",
                  !d.work && d.key !== date ? "opacity-50" : ""
                )}
              >
                <span className="capitalize">{d.dow}</span>
                <span className="text-sm font-semibold">{d.day}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setDate((d) => shiftDay(d, 1))} className="rounded-lg p-2 text-content-secondary hover:bg-subtle"><i className="ri-arrow-right-s-line" /></button>
            <button type="button" onClick={() => setDate((d) => shiftDay(d, 7))} className="rounded-lg p-2 text-content-secondary hover:bg-subtle" title="+7"><i className="ri-arrow-right-double-line" /></button>
          </div>
          <button type="button" onClick={() => setDate(todayKey())} className="h-9 rounded-lg border border-line bg-card px-3 text-xs text-content-secondary hover:bg-subtle">{t("mantenimiento.agenda.today")}</button>
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-9 rounded-lg border border-line bg-card px-2 text-sm text-content" aria-label={t("mantenimiento.fields.date")} />
          <span className="ml-auto hidden text-sm font-medium text-content first-letter:uppercase md:inline-block">{dayLabel}</span>
          <ViewSwitch value={view} onChange={setView} options={[{ id: "bays", icon: "ri-layout-column-line", label: t("mantenimiento.ui.agenda.byBay") }, { id: "list", icon: "ri-list-check-2", label: t("mantenimiento.ui.agenda.list") }]} />
        </>
      }
    >
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard icon="ri-calendar-event-line" tone="primary" label={t("mantenimiento.menu.agenda")} value={t("mantenimiento.ui.agenda.appointments", { count: active.length })} loading={loading} />
        <KpiCard icon="ri-check-double-line" tone="success" label={L.apptStatus("confirmado")} value={rows.filter((r) => r.status === "confirmado").length} loading={loading} />
        <KpiCard icon="ri-tools-line" tone="warning" label={L.apptStatus("en_proceso")} value={rows.filter((r) => r.status === "en_proceso").length} loading={loading} />
        <KpiCard icon="ri-community-line" tone="info" label={t("mantenimiento.ui.agenda.occupancy")} value={`${occupancy} %`} progress={occupancy} hint={config ? `${t("mantenimiento.agenda.bays")}: ${config.bays} · ${config.openTime}–${config.closeTime}` : undefined} loading={loading} />
      </div>

      {error ? <ErrorBlock message={error} onRetry={loadDay} /> : null}

      {loading && !rows.length ? (
        <LoadingBlock />
      ) : view === "bays" ? (
        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[1fr_340px]">
          <div className="overflow-x-auto rounded-xl border border-line bg-card">
            <div className="min-w-[720px]" style={{ display: "grid", gridTemplateColumns: `64px repeat(${bays}, minmax(160px, 1fr))` }}>
              <div className="sticky top-0 z-10 border-b border-line bg-surface" />
              {Array.from({ length: bays }, (_, i) => (
                <div key={i} className="sticky top-0 z-10 border-b border-l border-line bg-surface px-3 py-2 text-xs font-semibold text-content">
                  <i className="ri-tools-line mr-1 text-content-muted" />
                  {t("mantenimiento.ui.agenda.bay", { n: i + 1 })}
                  <span className="ml-1 font-normal text-content-muted">· {active.filter((r) => Number(r.bay) === i + 1).length}</span>
                </div>
              ))}
              {/* Columna de horas */}
              <div className="relative" style={{ height: hours.length * HOUR_PX }}>
                {hours.map((h, i) => (
                  <div key={h} className="absolute right-2 text-[11px] tabular-nums text-content-muted" style={{ top: Math.max(0, i * HOUR_PX - 6) }}>
                    {pad(Math.floor(h / 60))}:{pad(h % 60)}
                  </div>
                ))}
              </div>
              {Array.from({ length: bays }, (_, b) => (
                <div key={b} className="relative border-l border-line" style={{ height: hours.length * HOUR_PX }}>
                  {hours.map((h, i) => (
                    <div key={h} className="absolute inset-x-0 border-t border-dashed border-line/70" style={{ top: i * HOUR_PX }} />
                  ))}
                  {nowLine !== null && nowLine >= 0 && nowLine <= hours.length * HOUR_PX ? (
                    <div className="absolute inset-x-0 z-[5] border-t-2 border-error" style={{ top: nowLine }}>
                      {b === 0 ? <span className="absolute -left-1 -top-1.5 h-3 w-3 rounded-full bg-error" /> : null}
                    </div>
                  ) : null}
                  {sorted
                    .filter((r) => (Number(r.bay) || 1) === b + 1)
                    .map((r) => {
                      const top = Math.max(0, ((minutesOf(r.start) - open) / 60) * HOUR_PX);
                      const height = Math.max(30, ((Number(r.durationMinutes) || 30) / 60) * HOUR_PX - 4);
                      const short = height < 48;
                      const v = vehicleOf(r.vehicleId);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setSelectedId(r.id)}
                          className={cn("absolute inset-x-1 z-[6] overflow-hidden rounded-lg border-l-4 border px-2 py-1 text-left shadow-sm transition-shadow hover:shadow-md", BLOCK_TONES[r.status] ?? BLOCK_TONES.reservado, selectedId === r.id ? "ring-2 ring-primary-500" : "")}
                          style={{ top: top + 2, height }}
                        >
                          {short ? (
                            <p className="truncate text-[11px] text-content">
                              <b className="tabular-nums">{fmtTime(r.start, L.locale)}</b> · {r.serviceName || t("mantenimiento.agenda.service")}
                            </p>
                          ) : (
                          <div className="flex items-start gap-2">
                            {height >= 56 ? <VehicleThumb vehicle={v} size="xs" /> : null}
                            <div className="min-w-0">
                              <p className="truncate text-[11px] font-semibold tabular-nums text-content">{fmtTime(r.start, L.locale)}–{fmtTime(r.end, L.locale)}</p>
                              <p className="truncate text-xs font-medium text-content">{r.serviceName || t("mantenimiento.agenda.service")}</p>
                              {height >= 56 ? <p className="truncate text-[11px] text-content-secondary">{v?.label ?? r.vehicleId}</p> : null}
                            </div>
                          </div>
                          )}
                        </button>
                      );
                    })}
                </div>
              ))}
            </div>
            {rows.length === 0 ? <p className="border-t border-line px-4 py-3 text-center text-xs text-content-muted">{t("mantenimiento.agenda.dayEmpty")}</p> : null}
          </div>

          {/* Detalle de la cita seleccionada */}
          <div className="rounded-xl border border-line bg-card p-4">
            {selected ? (
              <div className="space-y-3">
                <Photo src={vehicleOf(selected.vehicleId)?.image} alt={vehicleOf(selected.vehicleId)?.label || ""} fallbackIcon="ri-truck-line" className="h-40 w-full rounded-lg" />
                <div>
                  <p className="text-sm font-semibold text-content">{selected.serviceName || t("mantenimiento.agenda.service")}</p>
                  <p className="text-xs text-content-secondary">{vehicleOf(selected.vehicleId)?.label ?? selected.vehicleId}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge tone={APPT_STATUS_TONES[selected.status] ?? "neutral"}>{L.apptStatus(selected.status)}</Badge>
                  {selected.bay ? <Badge tone="neutral">{t("mantenimiento.ui.agenda.bay", { n: selected.bay })}</Badge> : null}
                  {selected.workshopMode ? <Badge tone="neutral">{L.mode(selected.workshopMode)}</Badge> : null}
                </div>
                <p className="text-xs text-content-secondary">
                  <i className="ri-time-line mr-1" />
                  {fmtTime(selected.start, L.locale)}–{fmtTime(selected.end, L.locale)} · {selected.durationMinutes} min
                </p>
                {selected.notes ? <p className="text-xs text-content-secondary">{selected.notes}</p> : null}
                <ApptActions appt={selected} />
              </div>
            ) : (
              <p className="py-8 text-center text-xs text-content-muted">
                <i className="ri-cursor-line mb-2 block text-2xl" />
                {t("mantenimiento.agenda.hint")}
              </p>
            )}
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-card p-10 text-center text-sm text-content-muted">
          <i className="ri-calendar-2-line mb-2 block text-3xl" />
          {t("mantenimiento.agenda.dayEmpty")}
        </div>
      ) : (
        <div className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-card">
          {sorted.map((r) => {
            const v = vehicleOf(r.vehicleId);
            return (
              <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span className="w-24 shrink-0 text-sm font-semibold tabular-nums text-content">{fmtTime(r.start, L.locale)}–{fmtTime(r.end, L.locale)}</span>
                <VehicleThumb vehicle={v} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-content">{r.serviceName || t("mantenimiento.agenda.service")}</p>
                  <p className="truncate text-xs text-content-secondary">
                    {v?.label ?? r.vehicleId} · {r.durationMinutes} min{r.bay ? ` · ${t("mantenimiento.ui.agenda.bay", { n: r.bay })}` : ""}
                    {r.notes ? ` · ${r.notes}` : ""}
                  </p>
                </div>
                <Badge tone={APPT_STATUS_TONES[r.status] ?? "neutral"}>{L.apptStatus(r.status)}</Badge>
                <ApptActions appt={r} />
              </div>
            );
          })}
        </div>
      )}

      {modal?.kind === "reserve" && config ? (
        <Modal
          title={t("mantenimiento.agenda.reserve")}
          onClose={() => setModal(null)}
          themeClasses={tc}
          content={<AppointmentModal mode="create" vehicles={vehicles} services={services} defaultDurationMinutes={config.defaultDurationMinutes} defaultDate={date} themeClasses={tc} onCancel={() => setModal(null)} onSubmit={handleCreate} />}
        />
      ) : null}

      {modal?.kind === "reschedule" && config ? (
        <Modal
          title={t("mantenimiento.agenda.reschedule")}
          onClose={() => setModal(null)}
          themeClasses={tc}
          content={<AppointmentModal mode="edit" initial={modal.appt} vehicles={vehicles} services={services} defaultDurationMinutes={config.defaultDurationMinutes} defaultDate={date} themeClasses={tc} onCancel={() => setModal(null)} onSubmit={(values) => handleReschedule(modal.appt.id, values)} />}
        />
      ) : null}

      {modal?.kind === "config" && config ? (
        <Modal title={t("mantenimiento.agenda.settings")} onClose={() => setModal(null)} themeClasses={tc} content={<ScheduleConfigModal config={config} themeClasses={tc} onCancel={() => setModal(null)} onSubmit={handleSaveConfig} />} />
      ) : null}
    </PageShell>
  );
}
